import React, { useEffect, useState } from "react";
import api from "../../utils/axiosInstance";
import { useModal } from "../../context/ModalContext";

const timezones = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    // Fallback for older browsers
    return [
      "UTC",
      "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
      "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin",
      "Asia/Kolkata", "Asia/Shanghai", "Asia/Tokyo", "Asia/Dubai",
      "Australia/Sydney", "Pacific/Auckland",
    ];
  }
})();

const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default function GeneralTab() {
  const modal = useModal();
  const [settings, setSettings] = useState({ language: "en", timezone: detectedTimezone });

  useEffect(() => {
    api.get("/api/settings/general").then(res => {
      setSettings(prev => ({
        ...prev,
        ...res.data,
        timezone: res.data.timezone || detectedTimezone,
      }));
    }).catch(err => console.error(err));
  }, []);

  const updateGeneral = async (e) => {
    e.preventDefault();
    try {
      await api.put("/api/settings/general", settings);
      modal.alert("Settings updated!", "success");
    } catch (err) {
      console.error(err);
      modal.alert("Error updating settings", "error");
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-ink-primary mb-5">General Settings</h2>
      <form onSubmit={updateGeneral} className="space-y-5 max-w-3xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="input-group">
            <label className="input-label">Language</label>
            <select
              className="input"
              value={settings.language}
              onChange={(e) => setSettings({ ...settings, language: e.target.value })}
            >
              <option value="en">English</option>
              <option value="hi">Hindi</option>
              <option value="es">Spanish</option>
            </select>
            <p className="input-hint">Interface language for your account.</p>
          </div>
          <div className="input-group">
            <label className="input-label">Timezone</label>
            <select
              className="input"
              value={settings.timezone}
              onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
            >
              {timezones.map((tz) => (
                <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
              ))}
            </select>
            <p className="input-hint">Detected: {detectedTimezone.replace(/_/g, " ")}</p>
          </div>
        </div>

        {/* Anti-ban warmup */}
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 mt-0.5 accent-amber-500"
              checked={settings.warmupEnabled !== false}
              onChange={(e) => setSettings({ ...settings, warmupEnabled: e.target.checked })}
            />
            <span>
              <span className="text-sm font-medium text-ink-primary">New-number warmup (recommended)</span>
              <p className="text-xs text-ink-muted mt-1">
                A freshly linked number starts at 10 messages/day and ramps up to 30 over its first month.
                This protects the number while WhatsApp builds trust in it.
              </p>
            </span>
          </label>

          {settings.warmupEnabled === false && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              <strong>⚠️ Turning warmup off can get this WhatsApp number banned.</strong> A new number
              would send at its full daily limit (25–30/day) from day one instead of ramping from 10 —
              this is a common cause of bans, and a banned number is usually unrecoverable. Only disable
              this if the number has already been warmed up outside Walytic.
            </div>
          )}
        </div>

        {/* Daily send cap */}
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 mt-0.5 accent-amber-500"
              checked={settings.dailyCapEnabled !== false}
              onChange={(e) => setSettings({ ...settings, dailyCapEnabled: e.target.checked })}
            />
            <span>
              <span className="text-sm font-medium text-ink-primary">Daily send limit (recommended)</span>
              <p className="text-xs text-ink-muted mt-1">
                Caps each number at 25–30 messages/day. This is the main protection against a ban for
                oversending.
              </p>
            </span>
          </label>

          {settings.dailyCapEnabled === false && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              <strong>⚠️ Removing the daily limit can get this WhatsApp number banned.</strong> With it off,
              campaigns and flows can send continuously (only the 30/minute ceiling remains), which is a
              common cause of a permanent ban. Leave this on unless you know the number can handle the volume.
            </div>
          )}
        </div>

        <button className="btn-primary" type="submit">Save Settings</button>
      </form>
    </div>
  );
}
